// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import * as R from 'ramda';

// Helpers
import classNames from 'classnames';
import {
  getKey,
  getDisplay,
  createValue,
  getBodyTypesText
} from 'Common/formValues';
import { gtmEventPush } from 'utils/gtm/events';

// Components
import Icon from 'Common/Icon';
import CarType from 'Common/CarType';

/**
 * <CheckBoxGroup /> component.
 */
class CheckBoxGroup extends Component<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      filterValue: ''
    };
    this.timeouts = [];
  }

  componentWillUnmount() {
    this.timeouts.forEach(clearTimeout);
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {object} styles - Input styles.
   * @prop {object} bootstrap - bootstrap styles
   * @prop {string} name - input name.
   * @prop {string} label - label
   * @prop {string} selectedValue - value
   * @prop {array} options - options
   * @prop {bool} disableCounts - disableCounts
   * @prop {func} onSelect - onSelect
   * @prop {func} setAsSelected - setAsSelected
   * @prop {bool} isSelected - isSelected
   * @prop {bool} isSwoosh - isSwoosh
   * @prop {func} resetField - resetField
   * @prop {bool} isDisabled - isDisabled
   * @prop {bool} isCarTypes - isCarTypes
   */
  static propTypes = {
    styles: PropTypes.object.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    selectedValue: PropTypes.string,
    options: PropTypes.array,
    disableCounts: PropTypes.bool,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    setAsSelected: PropTypes.func,
    isSwoosh: PropTypes.bool,
    resetField: PropTypes.func,
    isDisabled: PropTypes.bool,
    disableFiltering: PropTypes.bool,
    isCarTypes: PropTypes.bool
  };

  /**
   * updateFilterValue.
   * @param {string} value - value.
   *
   * @return {void}
   */
  updateFilterValue = (e: SyntheticEvent) => {
    this.setState({
      filterValue: e.target.value
    });
  };

  handleFilterInputBlur = () => {
    const { label } = this.props;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: `${label}: value manually entered`
      }
    });
  };

  handleSelected = e => {
    const { label } = this.props;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: `${label}: menu opened`
      }
    });
    this.props.onSelect(e);
  };

  handleSelectValue = () => {
    const { label, name, resetField, setAsSelected } = this.props;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: `${label}: value selected`
      }
    });
    if (name === 'make' && resetField) {
      resetField('model');
      resetField('trim');
    }
    if (name === 'model' && resetField) {
      resetField('trim');
    }
    if (setAsSelected) {
      setTimeout(() => setAsSelected(''), 250);
    }
  };

  clearSelectedFilter = () => {
    const { label, name, resetField, setAsSelected } = this.props;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: `${label}: clear`
      }
    });
    resetField(name);
    setAsSelected('');
  };

  /**
   * Renders <Input /> component.
   *
   * @return {JSX} - renders an Input.
   */
  render() {
    const {
      styles,
      name,
      label,
      selectedValue,
      options,
      disableCounts,
      isSelected,
      isSwoosh,
      resetField,
      isDisabled,
      disableFiltering,
      isCarTypes
    } = this.props;

    const filteredOptions = options.filter(option => {
      if (typeof option.key === 'string') {
        return R.contains(
          this.state.filterValue.toLowerCase(),
          option.key.toLowerCase()
        );
      }
      return true;
    });

    /**
     * Renders <isChecked.
     * @param {object} item - item.
     * @param {boolean} selectedValue - selectedValue.
     *
     * @return {boolean} - returns isChecked
     */
    const isChecked = (item, selectedValue) => {
      if (item.key) {
        return item.key === getKey(selectedValue);
      }

      return false;
    };

    const isOptions = () => options.length !== 0;

    return (
      <div
        className={styles.checkBoxGroup}
        data-selectedcheckboxgroup={isSelected}
        data-name={name}
      >
        <button
          type="button"
          onClick={this.handleSelected}
          disabled={isDisabled || !isOptions()}
        >
          {label}{' '}
          {selectedValue && (
            <span> ({getBodyTypesText(getDisplay(selectedValue))})</span>
          )}
          <Icon
            iconName={isSwoosh ? 'angle-left' : 'angle-down'}
            size={'default'}
            className={classNames(
              styles.icon,
              !isSwoosh ? styles.iconDown : null
            )}
          />
        </button>
        {isSelected && (
          <div
            className={classNames(
              styles.checkboxes,
              isSwoosh ? styles.swoosh : null
            )}
          >
            {isSwoosh && <span className={styles.swooshLabel}>{label}</span>}
            {!disableFiltering && (
              <input
                className={styles.filter}
                type="text"
                placeholder="Type to filter"
                value={this.state.filterValue}
                onChange={this.updateFilterValue}
                onBlur={this.handleFilterInputBlur}
              />
            )}
            <div
              className={classNames(
                styles.checkboxesInner,
                isCarTypes ? styles.checkboxesInnerCarTypes : null
              )}
            >
              {!isCarTypes && (
                <div>
                  {filteredOptions.map((item, index) => {
                    return (
                      <div key={index}>
                        <label
                          className={classNames(
                            styles.container,
                            item.key && item.key === getKey(selectedValue)
                              ? styles.isSelected
                              : null
                          )}
                          key={index}
                        >
                          {item.display || item.key.toString()}
                          {!disableCounts && (
                            <span className={styles.count}>
                              ({item.doc_count.toLocaleString('en')})
                            </span>
                          )}
                          <Field
                            name={name}
                            className={styles.checkBox}
                            value={createValue(item)}
                            placeholder={label}
                            onClick={this.handleSelectValue}
                            component="input"
                            type={'radio'}
                            checked={isChecked(item, selectedValue)}
                          />
                          <span className={styles.checkmark} />
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}

              {isCarTypes &&
                filteredOptions.map((item, index) => {
                  return (
                    <div key={index}>
                      <CarType
                        key={index}
                        name={'category'}
                        size={'large'}
                        label={item.key}
                        results={item.doc_count || 0}
                        selectedValue={selectedValue}
                        value={createValue(item)}
                        isResults={true}
                      />
                    </div>
                  );
                })}
            </div>
            {resetField && (
              <button
                className={classNames(
                  styles.clearBtn,
                  isSwoosh ? styles.clearBtnSwoosh : null
                )}
                onClick={this.clearSelectedFilter}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default CheckBoxGroup;



// WEBPACK FOOTER //
// ./src/Results/CheckBoxGroup/index.js